import { useCallback, useEffect, useState } from 'react'

import {
  deleteKpiAssignment,
  getKpiAssignmentMetaData,
  getKpiAssignments,
  postKpiAssignments,
} from '@/api/kpi'
import {
  KpiAssignmentBodyModel,
  KpiAssignmentCreationModel,
  KpiAssignmentMetaDataModel,
  KpiAssignmentMetaDataResponseModel,
  KpiAssignmentsModel,
} from '@/types'

interface UseKpiGoalAssignmentsReturn {
  metadata: KpiAssignmentMetaDataModel | null
  kpiAssignments: KpiAssignmentsModel[] | null
  loading: boolean
  error: Error | null
  fetchMetadata: (branchName: string) => Promise<void>
  fetchKpiAssignments: (branchName?: string) => Promise<void>
  createAssignments: (
    assignments: KpiAssignmentCreationModel[]
  ) => Promise<void>
  deleteAssignment: (id: string) => Promise<void>
}

const KPI_CATEGORY_MAPPING: Record<string, string> = {
  sales: 'Sales',
  labor: 'Labor',
  speed_of_service: 'Speed Of Service',
  loyalty: 'Loyalty',
  spend: 'Spend',
} as const

export default function useKpiGoalAssignments(
  initialBranchName?: string
): UseKpiGoalAssignmentsReturn {
  const [metadata, setMetadata] = useState<KpiAssignmentMetaDataModel | null>(
    null
  )
  const [kpiAssignments, setKpiAssignments] = useState<
    KpiAssignmentsModel[] | null
  >(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchMetadata = useCallback(async (branchName: string) => {
    if (!branchName) {
      setMetadata(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data: KpiAssignmentMetaDataResponseModel =
        await getKpiAssignmentMetaData(branchName)

      const metadata: KpiAssignmentMetaDataModel = {
        branch: data.branch,
        eligibleUsers: data.eligible_users.map((user) => ({
          id: user.id,
          username: user.username,
          email: user.email,
          fullName: user.full_name,
        })),
        availableKpis: Object.fromEntries(
          Object.entries(data.available_kpis).map(([key, kpis]) => [
            KPI_CATEGORY_MAPPING[key] || key,
            kpis.map((kpi) => ({
              metricField: kpi.metric_field,
              name: kpi.name,
              unit: kpi.unit,
            })),
          ])
        ),
      }

      setMetadata(metadata)
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch KPI assignment metadata')
      setError(error)
      console.error('Error fetching KPI assignment metadata:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchKpiAssignments = useCallback(async (branchName?: string) => {
    setLoading(true)
    setError(null)

    try {
      const data = await getKpiAssignments(branchName)

      const assignments: KpiAssignmentsModel[] = data.map((assignment) => ({
        id: assignment.id,
        userId: assignment.user_id,
        userUsername: assignment.user_username,
        userEmail: assignment.user_email,
        userFullName: assignment.user_full_name,
        kpiMetric: assignment.kpi_metric,
        branchId: assignment.branch_id,
        branchName: assignment.branch_name,
        year: assignment.year,
        quarter: assignment.quarter,
        targetValue: assignment.target_value,
        bonusAmount: assignment.bonus_amount,
        actualValue: assignment.actual_value,
        achieved: assignment.achieved,
        progressPercent: assignment.progress_percent,
        createdAt: assignment.created_at,
        kpiName: assignment.kpi_name,
        kpiUnit: assignment.kpi_unit,
        kpiGroup:
          KPI_CATEGORY_MAPPING[assignment.kpi_group] || assignment.kpi_group,
      }))

      setKpiAssignments(assignments)
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch KPI assignments')
      setError(error)
      console.error('Error fetching KPI assignments:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const createAssignments = useCallback(
    async (newAssignments: KpiAssignmentCreationModel[]) => {
      setLoading(true)
      setError(null)

      const assignments: KpiAssignmentBodyModel[] = newAssignments.map(
        (assignment) => ({
          user: assignment.user,
          branch_name: assignment.branch_name,
          kpi_metric: assignment.kpiMetric,
          year: assignment.year,
          quarter: assignment.quarter,
          target_value: assignment.targetValue,
          bonus_amount: assignment.bonusAmount,
        })
      )

      try {
        await postKpiAssignments(assignments)
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to create KPI assignments')
        setError(error)
        console.error('Error creating KPI assignments:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const deleteAssignment = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      await deleteKpiAssignment(id)
      // Remove the deleted assignment from the local state
      setKpiAssignments((prev) =>
        prev ? prev.filter((assignment) => assignment.id !== id) : prev
      )
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to delete KPI assignment')
      setError(error)
      console.error('Error deleting KPI assignment:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial fetch if initialBranchName is provided
  useEffect(() => {
    if (initialBranchName) {
      fetchMetadata(initialBranchName)
    }
  }, []) // Only run on mount

  return {
    metadata,
    kpiAssignments,
    loading,
    error,
    fetchMetadata,
    fetchKpiAssignments,
    createAssignments,
    deleteAssignment,
  }
}
